import axios from 'axios';
import Cookies from 'js-cookie';
import store from '@store';
import router from '@router';

let isRefreshing = false;
let refreshPromise = null;
// 5-minute safety margin to anticipate the refresh and mitigate
// network latency or clock desynchronization between Client/Server.
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
const IDEMPOTENT_REFRESH_BACKOFF_MS = 5 * 60 * 1000;

/**
 * Refreshes the access token if expired.
 * Uses a single promise to handle concurrent requests during refresh.
 * @returns {Promise<string|null>} The refreshed access token or null if no token.
 */
export async function refreshToken() {
    const token = Cookies.get('access_token');

    if (!token) {
        return null;
    }

    const expireAt = Cookies.get('expire_at');
    const now = Date.now();

    const expireAtTimestamp = expireAt ? parseInt(expireAt, 10) : null;

    if (expireAtTimestamp && !isNaN(expireAtTimestamp) && now <= expireAtTimestamp) {
        return token;
    }

    if (!isRefreshing) {
        isRefreshing = true;

        refreshPromise = (async () => {
            const requestId = store.state.fingerprint && store.state.fingerprint.requestId
                ? store.state.fingerprint.requestId
                : null;
            const requestIdAt = store.state.fingerprint && store.state.fingerprint.requestIdAt
                ? store.state.fingerprint.requestIdAt
                : 0;
            const requestIdAgeMs = Date.now() - requestIdAt;
            const refreshTtlMs = 60 * 1000;
            const needsRefresh = !requestId || requestIdAgeMs > refreshTtlMs
                || requestId === 'fingerprint_error'
                || requestId === 'fingerprint_disabled';

            if (needsRefresh) {
                await store.dispatch('fetchFingerprint');
            }

            const refreshedRequestId = store.state.fingerprint && store.state.fingerprint.requestId
                ? store.state.fingerprint.requestId
                : null;

            const headerRequestId = refreshedRequestId || requestId || '';

            const header = {
                headers: {
                    Authorization: token,
                    'X-Fingerprint-RID': headerRequestId
                }
            };

            const params = new URLSearchParams();
            params.append('grant_type', 'refresh_token');

            const refreshTokenValue = Cookies.get('refresh_token');
            if (!refreshTokenValue) {
                throw new Error('Missing refresh token while trying to refresh access token');
            }
            params.append('refresh_token', refreshTokenValue);

            const response = await axios.post(
                process.env.VUE_APP_API_HOST + '/erp/oauth/token/refresh',
                params,
                header
            );

            const data = response.data;
            const isProduction = process.env.NODE_ENV === 'production';
            const securityOptions = {
                secure: isProduction,
                // sameSite: 'Lax' is intentionally used to support deep-linking
                // via external links (WhatsApp, Email, Reports). Additional protection
                // comes from custom headers (X-Fingerprint-RID) and strict CORS policies.
                sameSite: 'Lax',
                path: '/'
            };

            // Backend idempotent refresh: when the current token is still valid,
            // some environments may return 200 with an empty body.
            // Keep current token and push expire_at forward with a short backoff
            // to avoid immediate refresh loops.
            const isEmptyPayload = !data || (typeof data === 'object' && Object.keys(data).length === 0);
            if (isEmptyPayload) {
                const accessToken = Cookies.get("access_token");
                if (!accessToken) {
                    throw new Error('Refresh response without token and no access token in cookies');
                }
                Cookies.set("expire_at", (Date.now() + IDEMPOTENT_REFRESH_BACKOFF_MS).toString(), {
                    ...securityOptions,
                    expires: 7
                });
                return accessToken;
            }

            if (!data.access_token || !data.expires_in || typeof data.expires_in !== 'number') {
                throw new Error('Invalid refresh response payload');
            }

            const newExpireAt = Date.now() + (data.expires_in * 1000) - REFRESH_THRESHOLD_MS;
            const accessToken = "Bearer " + data.access_token;

            Cookies.set("access_token", accessToken, {
                ...securityOptions,
                expires: 7
            });
            store.commit('authSuccess', accessToken);

            Cookies.set("expire_at", newExpireAt.toString(), {
                ...securityOptions,
                expires: 7
            });

            if (data.refresh_token) {
                Cookies.set("refresh_token", data.refresh_token, {
                    ...securityOptions,
                    expires: 365
                });
            }

            return accessToken;
        })()
            .catch(async (err) => {
                await store.dispatch('logoutRequest');
                router.push('/');
                throw err;
            })
            .finally(() => {
                isRefreshing = false;
                refreshPromise = null;
            });
    }

    return await refreshPromise;
}
